import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/LanguageSelector";
import { 
  Luggage, 
  LayoutDashboard, 
  CalendarDays, 
  LogOut,
  Settings,
  Image,
  Type,
  DollarSign,
  Building,
  Globe,
  Save,
  Upload,
  Trash2,
  RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState({});
  
  const [settings, setSettings] = useState({
    // Business Info
    business_name: "Bari Bird Luggage Storage",
    business_address: "Via Andrea da Bari, 122, Bari, Italy",
    business_email: "estiakhossain90@gmail.com",
    business_phone: "+393205608143",
    google_maps_link: "https://www.google.com/maps/search/?api=1&query=Via+Andrea+da+Bari+122+Bari+Italy",
    
    // Pricing
    price_per_day: 5.0,
    price_per_hour: 1.5,
    
    // Hero Section - English
    hero_tagline_en: "Trusted Luggage Storage in Bari",
    hero_title1_en: "Travel Light,",
    hero_title2_en: "Explore Freely",
    hero_description_en: "Secure luggage storage in the heart of Bari. Drop your bags, pick up your freedom, and discover the beauty of Puglia.",
    
    // Hero Section - Italian
    hero_tagline_it: "Deposito Bagagli Affidabile a Bari",
    hero_title1_it: "Viaggia Leggero,",
    hero_title2_it: "Esplora Liberamente",
    hero_description_it: "Deposito bagagli sicuro nel cuore di Bari. Lascia le tue valigie, riprendi la tua libertà e scopri la bellezza della Puglia.",
    
    // Hero Section - Spanish
    hero_tagline_es: "Consigna de Equipaje de Confianza en Bari",
    hero_title1_es: "Viaja Ligero,",
    hero_title2_es: "Explora Libremente",
    hero_description_es: "Consigna de equipaje segura en el corazón de Bari. Deja tus maletas, recupera tu libertad y descubre la belleza de Puglia.",
    
    hero_image: "",
    
    // Features - English
    feature1_title_en: "100% Secure",
    feature1_desc_en: "Your luggage is stored in a locked, monitored facility with insurance coverage for peace of mind.",
    feature2_title_en: "Flexible Hours",
    feature2_desc_en: "Store your bags for hours or days. Pay only for the time you need with our flexible pricing.",
    feature3_title_en: "Central Location",
    feature3_desc_en: "Located in the heart of Bari, just minutes from the train station and historic old town.",
    
    // Features - Italian
    feature1_title_it: "100% Sicuro",
    feature1_desc_it: "I tuoi bagagli sono conservati in una struttura chiusa e monitorata con copertura assicurativa.",
    feature2_title_it: "Orari Flessibili",
    feature2_desc_it: "Deposita i tuoi bagagli per ore o giorni. Paga solo per il tempo necessario.",
    feature3_title_it: "Posizione Centrale",
    feature3_desc_it: "Situato nel cuore di Bari, a pochi minuti dalla stazione ferroviaria e dal centro storico.",
    
    // Features - Spanish
    feature1_title_es: "100% Seguro",
    feature1_desc_es: "Tu equipaje se guarda en una instalación cerrada y monitoreada con cobertura de seguro.",
    feature2_title_es: "Horarios Flexibles",
    feature2_desc_es: "Guarda tus maletas por horas o días. Paga solo por el tiempo que necesites.",
    feature3_title_es: "Ubicación Central",
    feature3_desc_es: "Ubicado en el corazón de Bari, a pocos minutos de la estación de tren y del casco antiguo.",
    
    feature_image1: "",
    feature_image2: "",
    
    // Footer
    footer_desc_en: "Your trusted luggage storage service in the heart of Bari, Italy.",
    footer_desc_it: "Il tuo servizio di deposito bagagli affidabile nel cuore di Bari, Italia.",
    footer_desc_es: "Tu servicio de consigna de equipaje de confianza en el corazón de Bari, Italia.",
  });

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const info = localStorage.getItem("admin_info");
    
    if (!token) {
      navigate("/admin/login");
      return;
    }
    
    if (info) {
      setAdminInfo(JSON.parse(info));
    }
    
    fetchSettings();
    fetchLogo();
  }, [navigate]);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
  });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/site-settings`, getAuthHeader());
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLogo = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      if (response.data.logo_url) {
        setLogoUrl(`${API}/logo?t=${Date.now()}`);
      }
    } catch (error) {
      console.log("No logo configured");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, settings, getAuthHeader());
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(prev => ({ ...prev, logo: true }));
    try {
      await axios.post(`${API}/admin/upload-logo`, formData, {
        ...getAuthHeader(),
        headers: { ...getAuthHeader().headers, "Content-Type": "multipart/form-data" }
      });
      toast.success("Logo uploaded!");
      setLogoUrl(`${API}/logo?t=${Date.now()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload");
    } finally {
      setUploading(prev => ({ ...prev, logo: false }));
    }
  };

  const handleImageUpload = async (event, imageType) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(prev => ({ ...prev, [imageType]: true }));
    try {
      await axios.post(`${API}/admin/upload-image/${imageType}`, formData, {
        ...getAuthHeader(),
        headers: { ...getAuthHeader().headers, "Content-Type": "multipart/form-data" }
      });
      toast.success(`${imageType} image uploaded!`);
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload");
    } finally {
      setUploading(prev => ({ ...prev, [imageType]: false }));
    }
  };

  const handleDeleteImage = async (imageType) => {
    try {
      await axios.delete(`${API}/admin/image/${imageType}`, getAuthHeader());
      toast.success("Image deleted");
      if (imageType === "hero") {
        setSettings(prev => ({ ...prev, hero_image: "" }));
      } else if (imageType === "feature1") {
        setSettings(prev => ({ ...prev, feature_image1: "" }));
      } else if (imageType === "feature2") {
        setSettings(prev => ({ ...prev, feature_image2: "" }));
      }
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/admin/logo`, getAuthHeader());
      toast.success("Logo deleted");
      setLogoUrl(null);
    } catch (error) {
      toast.error("Failed to delete logo");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_info");
    navigate("/admin/login");
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="spinner w-12 h-12 border-4"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-settings">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-64 bg-[#004E64] text-white z-40 hidden md:block">
        <div className="p-6 border-b border-white/10">
          <div className="flex flex-col items-center gap-2">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-12 w-auto object-contain" />
            ) : (
              <Luggage className="w-8 h-8 text-[#FFB400]" />
            )}
            <span className="font-serif text-sm font-semibold text-center">Admin Panel</span>
          </div>
        </div>
        
        <nav className="p-4">
          <button 
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center gap-3 px-4 py-3 rounded-xl font-semibold mb-2 w-full text-left text-white/70 hover:bg-white/10"
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </button>
          <button 
            onClick={() => navigate("/admin/dashboard")}
            className="flex items-center gap-3 px-4 py-3 rounded-xl font-semibold mb-2 w-full text-left text-white/70 hover:bg-white/10"
          >
            <CalendarDays className="w-5 h-5" />
            Bookings
          </button>
          <button 
            className="flex items-center gap-3 px-4 py-3 rounded-xl font-semibold mb-2 w-full text-left bg-[#FFB400] text-[#004E64]"
          >
            <Settings className="w-5 h-5" />
            Site Settings
          </button>
        </nav>
        
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="mb-4 px-4">
            <p className="text-sm text-white/50">Logged in as</p>
            <p className="text-white font-medium truncate">{adminInfo?.name || adminInfo?.email}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-white/70 hover:bg-white/10 rounded-xl w-full">
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-[#004E64] text-white p-4 z-40 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain" />
          ) : (
            <Luggage className="w-6 h-6 text-[#FFB400]" />
          )}
          <span className="font-semibold">Settings</span>
        </div>
        <button onClick={handleLogout} className="p-2">
          <LogOut className="w-5 h-5" />
        </button>
      </div>

      {/* Main Content */}
      <div className="md:ml-64 p-6 pt-20 md:pt-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-semibold text-[#004E64]">Site Settings</h1>
            <p className="text-slate-500">Manage all website content and configuration</p>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button onClick={handleSave} disabled={saving} className="bg-[#004E64] hover:bg-[#003848]" data-testid="save-settings-btn">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save All Changes
            </Button>
          </div>
        </div>

        <Tabs defaultValue="business" className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow-sm">
            <TabsTrigger value="business" className="data-[state=active]:bg-[#004E64] data-[state=active]:text-white rounded-lg">
              <Building className="w-4 h-4 mr-2" /> Business
            </TabsTrigger>
            <TabsTrigger value="hero" className="data-[state=active]:bg-[#004E64] data-[state=active]:text-white rounded-lg">
              <Type className="w-4 h-4 mr-2" /> Hero
            </TabsTrigger>
            <TabsTrigger value="features" className="data-[state=active]:bg-[#004E64] data-[state=active]:text-white rounded-lg">
              <Globe className="w-4 h-4 mr-2" /> Features
            </TabsTrigger>
            <TabsTrigger value="images" className="data-[state=active]:bg-[#004E64] data-[state=active]:text-white rounded-lg">
              <Image className="w-4 h-4 mr-2" /> Images
            </TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-[#004E64] data-[state=active]:text-white rounded-lg">
              <DollarSign className="w-4 h-4 mr-2" /> Pricing
            </TabsTrigger>
          </TabsList>

          {/* Business Tab */}
          <TabsContent value="business">
            <div className="bg-white rounded-2xl shadow-sm p-8 space-y-6">
              <h2 className="font-bold text-lg text-[#004E64] border-b pb-4">Business Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Business Name</Label>
                  <Input
                    value={settings.business_name}
                    onChange={(e) => updateSetting("business_name", e.target.value)}
                    className="mt-2 h-12"
                    data-testid="input-business-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Email</Label>
                  <Input
                    value={settings.business_email}
                    onChange={(e) => updateSetting("business_email", e.target.value)}
                    className="mt-2 h-12"
                    data-testid="input-business-email"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Phone / WhatsApp</Label>
                  <Input
                    value={settings.business_phone}
                    onChange={(e) => updateSetting("business_phone", e.target.value)}
                    className="mt-2 h-12"
                    data-testid="input-business-phone"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Address</Label>
                  <Input
                    value={settings.business_address}
                    onChange={(e) => updateSetting("business_address", e.target.value)}
                    className="mt-2 h-12"
                    data-testid="input-business-address"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Google Maps Link</Label>
                  <Input
                    value={settings.google_maps_link}
                    onChange={(e) => updateSetting("google_maps_link", e.target.value)}
                    className="mt-2 h-12"
                  />
                </div>
              </div>

              {/* Logo Section */}
              <div className="pt-6 border-t">
                <h3 className="font-bold text-[#004E64] mb-4">Company Logo</h3>
                <div className="flex items-start gap-6">
                  <div className="w-32 h-32 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center bg-slate-50">
                    {logoUrl ? (
                      <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain p-2" />
                    ) : (
                      <Luggage className="w-12 h-12 text-slate-300" />
                    )}
                  </div>
                  <div className="space-y-3">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploading.logo}
                      className="w-64"
                      data-testid="logo-upload"
                    />
                    <p className="text-xs text-slate-400">PNG, JPEG, WebP or SVG. Max 2MB.</p>
                    {logoUrl && (
                      <Button variant="outline" size="sm" onClick={handleDeleteLogo} className="text-red-500">
                        <Trash2 className="w-4 h-4 mr-2" /> Remove Logo
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Hero Tab */}
          <TabsContent value="hero">
            <div className="bg-white rounded-2xl shadow-sm p-8 space-y-8">
              <h2 className="font-bold text-lg text-[#004E64] border-b pb-4">Hero Section Content</h2>
              
              {/* English */}
              <div className="space-y-4">
                <h3 className="font-bold text-[#004E64] flex items-center gap-2">🇬🇧 English</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Tagline</Label>
                    <Input value={settings.hero_tagline_en} onChange={(e) => updateSetting("hero_tagline_en", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Title Line 1</Label>
                    <Input value={settings.hero_title1_en} onChange={(e) => updateSetting("hero_title1_en", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Title Line 2 (Highlighted)</Label>
                    <Input value={settings.hero_title2_en} onChange={(e) => updateSetting("hero_title2_en", e.target.value)} className="mt-2" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Description</Label>
                  <Textarea value={settings.hero_description_en} onChange={(e) => updateSetting("hero_description_en", e.target.value)} className="mt-2" rows={3} />
                </div>
              </div>

              {/* Italian */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="font-bold text-[#004E64] flex items-center gap-2">🇮🇹 Italiano</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Tagline</Label>
                    <Input value={settings.hero_tagline_it} onChange={(e) => updateSetting("hero_tagline_it", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Title Line 1</Label>
                    <Input value={settings.hero_title1_it} onChange={(e) => updateSetting("hero_title1_it", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Title Line 2</Label>
                    <Input value={settings.hero_title2_it} onChange={(e) => updateSetting("hero_title2_it", e.target.value)} className="mt-2" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Description</Label>
                  <Textarea value={settings.hero_description_it} onChange={(e) => updateSetting("hero_description_it", e.target.value)} className="mt-2" rows={3} />
                </div>
              </div>

              {/* Spanish */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="font-bold text-[#004E64] flex items-center gap-2">🇪🇸 Español</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Tagline</Label>
                    <Input value={settings.hero_tagline_es} onChange={(e) => updateSetting("hero_tagline_es", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Title Line 1</Label>
                    <Input value={settings.hero_title1_es} onChange={(e) => updateSetting("hero_title1_es", e.target.value)} className="mt-2" />
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Title Line 2</Label>
                    <Input value={settings.hero_title2_es} onChange={(e) => updateSetting("hero_title2_es", e.target.value)} className="mt-2" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Description</Label>
                  <Textarea value={settings.hero_description_es} onChange={(e) => updateSetting("hero_description_es", e.target.value)} className="mt-2" rows={3} />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features">
            <div className="bg-white rounded-2xl shadow-sm p-8 space-y-8">
              <h2 className="font-bold text-lg text-[#004E64] border-b pb-4">Features Section</h2>
              
              {/* Feature 1 */}
              <div className="space-y-4">
                <h3 className="font-bold text-[#004E64]">Feature 1 - Security</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm text-slate-500">🇬🇧 Title</Label>
                    <Input value={settings.feature1_title_en} onChange={(e) => updateSetting("feature1_title_en", e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">🇮🇹 Title</Label>
                    <Input value={settings.feature1_title_it} onChange={(e) => updateSetting("feature1_title_it", e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">🇪🇸 Title</Label>
                    <Input value={settings.feature1_title_es} onChange={(e) => updateSetting("feature1_title_es", e.target.value)} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Textarea value={settings.feature1_desc_en} onChange={(e) => updateSetting("feature1_desc_en", e.target.value)} placeholder="English description" rows={2} />
                  <Textarea value={settings.feature1_desc_it} onChange={(e) => updateSetting("feature1_desc_it", e.target.value)} placeholder="Italian description" rows={2} />
                  <Textarea value={settings.feature1_desc_es} onChange={(e) => updateSetting("feature1_desc_es", e.target.value)} placeholder="Spanish description" rows={2} />
                </div>
              </div>

              {/* Feature 2 */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="font-bold text-[#004E64]">Feature 2 - Flexibility</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm text-slate-500">🇬🇧 Title</Label>
                    <Input value={settings.feature2_title_en} onChange={(e) => updateSetting("feature2_title_en", e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">🇮🇹 Title</Label>
                    <Input value={settings.feature2_title_it} onChange={(e) => updateSetting("feature2_title_it", e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">🇪🇸 Title</Label>
                    <Input value={settings.feature2_title_es} onChange={(e) => updateSetting("feature2_title_es", e.target.value)} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Textarea value={settings.feature2_desc_en} onChange={(e) => updateSetting("feature2_desc_en", e.target.value)} placeholder="English description" rows={2} />
                  <Textarea value={settings.feature2_desc_it} onChange={(e) => updateSetting("feature2_desc_it", e.target.value)} placeholder="Italian description" rows={2} />
                  <Textarea value={settings.feature2_desc_es} onChange={(e) => updateSetting("feature2_desc_es", e.target.value)} placeholder="Spanish description" rows={2} />
                </div>
              </div>

              {/* Feature 3 */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="font-bold text-[#004E64]">Feature 3 - Location</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm text-slate-500">🇬🇧 Title</Label>
                    <Input value={settings.feature3_title_en} onChange={(e) => updateSetting("feature3_title_en", e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">🇮🇹 Title</Label>
                    <Input value={settings.feature3_title_it} onChange={(e) => updateSetting("feature3_title_it", e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">🇪🇸 Title</Label>
                    <Input value={settings.feature3_title_es} onChange={(e) => updateSetting("feature3_title_es", e.target.value)} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Textarea value={settings.feature3_desc_en} onChange={(e) => updateSetting("feature3_desc_en", e.target.value)} placeholder="English description" rows={2} />
                  <Textarea value={settings.feature3_desc_it} onChange={(e) => updateSetting("feature3_desc_it", e.target.value)} placeholder="Italian description" rows={2} />
                  <Textarea value={settings.feature3_desc_es} onChange={(e) => updateSetting("feature3_desc_es", e.target.value)} placeholder="Spanish description" rows={2} />
                </div>
              </div>

              {/* Footer */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="font-bold text-[#004E64]">Footer Description</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm text-slate-500">🇬🇧 English</Label>
                    <Textarea value={settings.footer_desc_en} onChange={(e) => updateSetting("footer_desc_en", e.target.value)} className="mt-1" rows={2} />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">🇮🇹 Italiano</Label>
                    <Textarea value={settings.footer_desc_it} onChange={(e) => updateSetting("footer_desc_it", e.target.value)} className="mt-1" rows={2} />
                  </div>
                  <div>
                    <Label className="text-sm text-slate-500">🇪🇸 Español</Label>
                    <Textarea value={settings.footer_desc_es} onChange={(e) => updateSetting("footer_desc_es", e.target.value)} className="mt-1" rows={2} />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images">
            <div className="bg-white rounded-2xl shadow-sm p-8 space-y-8">
              <h2 className="font-bold text-lg text-[#004E64] border-b pb-4">Website Images</h2>
              
              {/* Hero Image */}
              <div className="space-y-4">
                <h3 className="font-bold text-[#004E64]">Hero Background Image</h3>
                <div className="flex items-start gap-6">
                  <div className="w-48 h-32 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden">
                    {settings.hero_image ? (
                      <img src={settings.hero_image} alt="Hero" className="w-full h-full object-cover" />
                    ) : (
                      <Image className="w-12 h-12 text-slate-300" />
                    )}
                  </div>
                  <div className="space-y-3">
                    <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "hero")} disabled={uploading.hero} className="w-64" data-testid="hero-image-upload" />
                    <p className="text-xs text-slate-400">Recommended: 1920x1080px. Max 5MB.</p>
                    {settings.hero_image && (
                      <Button variant="outline" size="sm" onClick={() => handleDeleteImage("hero")} className="text-red-500">
                        <Trash2 className="w-4 h-4 mr-2" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Feature Image 1 */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="font-bold text-[#004E64]">Feature Image 1</h3>
                <div className="flex items-start gap-6">
                  <div className="w-48 h-32 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden">
                    {settings.feature_image1 ? (
                      <img src={settings.feature_image1} alt="Feature 1" className="w-full h-full object-cover" />
                    ) : (
                      <Image className="w-12 h-12 text-slate-300" />
                    )}
                  </div>
                  <div className="space-y-3">
                    <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "feature1")} disabled={uploading.feature1} className="w-64" />
                    <p className="text-xs text-slate-400">Recommended: 800x600px. Max 5MB.</p>
                    {settings.feature_image1 && (
                      <Button variant="outline" size="sm" onClick={() => handleDeleteImage("feature1")} className="text-red-500">
                        <Trash2 className="w-4 h-4 mr-2" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Feature Image 2 */}
              <div className="space-y-4 pt-6 border-t">
                <h3 className="font-bold text-[#004E64]">Feature Image 2</h3>
                <div className="flex items-start gap-6">
                  <div className="w-48 h-32 border-2 border-dashed border-slate-200 rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden">
                    {settings.feature_image2 ? (
                      <img src={settings.feature_image2} alt="Feature 2" className="w-full h-full object-cover" />
                    ) : (
                      <Image className="w-12 h-12 text-slate-300" />
                    )}
                  </div>
                  <div className="space-y-3">
                    <Input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "feature2")} disabled={uploading.feature2} className="w-64" />
                    <p className="text-xs text-slate-400">Recommended: 800x600px. Max 5MB.</p>
                    {settings.feature_image2 && (
                      <Button variant="outline" size="sm" onClick={() => handleDeleteImage("feature2")} className="text-red-500">
                        <Trash2 className="w-4 h-4 mr-2" /> Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <div className="bg-white rounded-2xl shadow-sm p-8 space-y-6">
              <h2 className="font-bold text-lg text-[#004E64] border-b pb-4">Pricing Configuration</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-xl">
                <div>
                  <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Price per Bag per Day (€)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={settings.price_per_day}
                    onChange={(e) => updateSetting("price_per_day", parseFloat(e.target.value) || 0)}
                    className="mt-2 h-12 text-xl font-bold"
                    data-testid="input-price-day"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold text-[#004E64]/70 uppercase">Price per Bag per Hour (€)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={settings.price_per_hour}
                    onChange={(e) => updateSetting("price_per_hour", parseFloat(e.target.value) || 0)}
                    className="mt-2 h-12 text-xl font-bold"
                    data-testid="input-price-hour"
                  />
                </div>
              </div>
              
              <div className="bg-[#F5F5F0] rounded-xl p-6 max-w-xl">
                <h4 className="font-bold text-[#004E64] mb-4">Pricing Preview</h4>
                <div className="space-y-2 text-[#5A6B7C]">
                  <p>Daily rate: <span className="font-bold text-[#004E64]">€{settings.price_per_day}</span> per bag per day</p>
                  <p>Hourly rate: <span className="font-bold text-[#004E64]">€{settings.price_per_hour}</span> per bag per hour</p>
                  <p className="text-sm pt-2 border-t mt-2">*Hourly pricing applies for same-day storage only</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
